import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CartModal from '../components/user/CartModal';
import LoginModal from '../components/user/LoginModal';
import RegisterModal from '../components/user/RegisterModal';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, getImageUrl } from '../utils/config';

const StoreAboutPage = () => {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const [store, setStore] = useState(null);
  const [aboutSections, setAboutSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchStoreAbout();
    checkAuthStatus();
  }, [slug]);

  useEffect(() => {
    if (store && store.redirect_url && !isAdmin) {
      window.location.href = store.redirect_url;
      return;
    }
  }, [store, isAdmin]);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    setIsLoggedIn(!!token);
  };

  // تحديث عدد العناصر في البادج
  const updateCartCount = async () => {
    if (!store) return;
    if (isLoggedIn) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(getApiUrl(`/cart/${store.id}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCartCount(response.data.data?.items?.length || 0);
      } catch (error) {
        setCartCount(0);
      }
    } else {
      const localCartKey = `cart_${store?.id}`;
      const localCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');
      setCartCount(localCart.reduce((total, item) => total + item.quantity, 0));
    }
  };

  // تحديث handleAddToCart ليحدث البادج فورًا
  const handleAddToCart = async (product) => {
    if (!isLoggedIn) {
      const localCartKey = `cart_${store.id}`;
      const existingCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');

      // Check if product already exists in cart
      const existingItemIndex = existingCart.findIndex(item => item.id === product.id);

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        existingCart[existingItemIndex].quantity += 1;
      } else {
        // Add new item to cart
        existingCart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          discount_percentage: product.discount_percentage,
          image: product.image,
          quantity: 1
        });
      }

      // Save updated cart to localStorage
      localStorage.setItem(localCartKey, JSON.stringify(existingCart));

      // Trigger re-render to update cart count
      updateCartCount(); // تحديث البادج

      // Show success message
      toast.success('Product added to cart successfully!', {
        autoClose: 1000,
      });
      return;
    }

    // If logged in, add to backend cart
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl(`/cart/${store.id}/add`), {
        product_id: product.id,
        quantity: 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show success message
      toast.success('Product added to cart successfully!', {
        autoClose: 1000,
      });
      updateCartCount(); // تحديث البادج
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add product to cart', {
        autoClose: 1000,
      });
    }
  };

  // دالة حذف عنصر من الكارت وتحديث البادج
  const handleRemoveFromCart = (productId) => {
    const localCartKey = `cart_${store.id}`;
    const existingCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');
    const updatedCart = existingCart.filter(item => item.id !== productId);
    localStorage.setItem(localCartKey, JSON.stringify(updatedCart));
    updateCartCount(); // تحديث البادج
  };

  // تحديث getCartCount ليقرأ من localStorage دائمًا للضيوف
  const getCartCount = () => {
    if (isLoggedIn) {
      // For logged in users, we'll need to fetch from backend
      // For now, return 0 as we don't have real-time sync
      return 0;
    } else {
      const localCartKey = `cart_${store?.id}`;
      const localCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');
      return localCart.reduce((total, item) => total + item.quantity, 0);
    }
  };

  const handleCartUpdate = () => {
    // Trigger re-render to update cart count
    updateCartCount();
  };

  const fetchStoreAbout = async () => {
    try {
      setLoading(true);

      // Fetch store details
      const storeResponse = await axios.get(getApiUrl(`/stores/${slug}`));
      setStore(storeResponse.data.data);

      // Fetch about sections
      const aboutResponse = await axios.get(getApiUrl(`/stores/${slug}/about`));
      setAboutSections(aboutResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching store about:', error);
      setError('Failed to load store information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading store information...</p>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Store not found'}</p>
          <Link
            to="/"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Back to Stores
          </Link>
        </div>
      </div>
    );
  }

  // If store has redirect_url and user is not admin, stay in loading
  if (store && store.redirect_url && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to={`/stores/${slug}`} className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                {store.icon ? (
                  <img
                    src={getImageUrl(store.icon)}
                    alt={store.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <h1 className="text-xl font-semibold text-gray-900">{store.name}</h1>
            </Link>
          </div>

          {/* Phone Number */}
          {store.phone && (
            <div className="flex items-center space-x-2 text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm font-medium">{store.phone}</span>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Store Information */}
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* About Sections */}
      {aboutSections.length > 0 ? (
        <div className="space-y-6">
          {aboutSections.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
              <div className="prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No information available</h3>
          <p className="text-gray-500">This store hasn't added any about information yet.</p>
        </div>
      )}
    </div>

    {/* Bottom Navigation Bar */}
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-around h-16">
          <Link
            to={`/stores/${slug}`}
            className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xs font-medium">Home</span>
          </Link>

          <Link
            to={`/stores/${slug}/about`}
            className="flex flex-col items-center space-y-1 text-emerald-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-medium">About</span>
          </Link>

          {/* Cart Button */}
          <button
            onClick={() => setShowCart(true)}
            className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900 relative"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
            <span className="text-xs font-medium">Cart</span>
          </button>

          {/* Profile/Login Button */}
          {isLoggedIn ? (
            <Link
              to={`/stores/${slug}/account`}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-medium">Account</span>
            </Link>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-medium">Account</span>
            </button>
          )}
        </div>
      </div>
    </nav>

    {/* Modals */}
    {showCart && (
      <CartModal
        storeId={store.id}
        onClose={() => setShowCart(false)}
        onCartUpdate={handleCartUpdate}
      />
    )}

    {showLogin && (
      <LoginModal
        onClose={() => setShowLogin(false)}
        onRegisterClick={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
      />
    )}

    {showRegister && (
      <RegisterModal
        onClose={() => setShowRegister(false)}
        onLoginClick={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
    )}
    </div>
  );
};

export default StoreAboutPage; 